import { PipeSeriesRepository } from "@imi-ernd-be/hyselect-engine";
import { GetUsersPreferencesPipeSeriesStatus200 } from "@imi/api-client/index.js";
import { CustomPipeSeries } from "@src/core/api/customPipeSeries";
import { PipeSeriesPreference } from "@src/core/api/pipeSeriesPreference";
import { PipeResults } from "@src/routes/projects/$projectId/selections/components/PipeResultsCard";
import { mergeWithDefaults } from "@src/shared/helpers/mergeWithDefaults";
import { assert } from "radashi";
import { engine } from "..";
import { extractDefaultValues } from "../helpers/extractDefaultValues";
import { getNumberDefinition } from "../helpers/fieldDefinitions/getNumberDefinition";
import {
  getOptionsFieldDefinition,
  OptionsFieldDefinition,
} from "../helpers/fieldDefinitions/getOptionsFieldDefinition";
import { TextFieldDefinition } from "../helpers/fieldDefinitions/getTextDefinition";
import { trackLiveObjects } from "../helpers/trackLiveObjects";
import { withCache } from "../helpers/withCache";
import { withDelete } from "../helpers/withDelete";
import { buildPipeSerie, buildPipeSeries } from "./buildPipeSeries";
import { normalizeCustomSeries } from "./normalizeCustomSeries";
import { setCustomPipeSeries } from "./setCustomPipeSeries";
import { setExistingPipeSeries } from "./setExistingPipeSeries";

export type AddPipeSeriesDefinitions = ReturnType<
  typeof pipesRepository.getAddDefinitions
>;
export type PipeSeriesDefinitions = ReturnType<
  typeof pipesRepository.getDefinitions
>;

export const pipesRepository = trackLiveObjects("pipesRepository", {
  getAddDefinitions() {
    const repository = new engine.PipeSeriesRepository();
    const series = repository.createPipeSerie();
    assert(series, "Failed to create pipe series");

    const pipe = series.createPipe();
    assert(pipe, "Failed to create pipe");

    return withDelete([repository, series, pipe], {
      id: {
        default: crypto.randomUUID(),
        disabled: false,
        type: "text",
      } satisfies TextFieldDefinition,
      name: {
        default: "",
        disabled: false,
        type: "text",
      } satisfies TextFieldDefinition,
      pipes: {
        id: {
          default: crypto.randomUUID(),
          disabled: false,
          type: "text",
        } satisfies TextFieldDefinition,
        internalDiameter: getNumberDefinition(pipe.getInternalDiameter()),
        maxPressure: getNumberDefinition(pipe.getMaxPressure()),
        maxTemperature: getNumberDefinition(pipe.getMaxTemperature()),
        name: {
          default: "",
          disabled: false,
          type: "text",
        } satisfies TextFieldDefinition,
        roughness: getNumberDefinition(pipe.getRoughness()),
        valveSize: getOptionsFieldDefinition(pipe.valveSizes()),
      },
    });
  },
  getAllPipeSeries: () =>
    withCache("getAllPipeSeries", () => {
      const repository = new engine.PipeSeriesRepository();
      repository.fillPipeSeries();
      return withDelete(
        repository,
        buildPipeSeries(repository.getPipeSeries()),
      );
    }),
  getDefaultPipeSeries: () =>
    withCache("getDefaultPipeSeries", () => {
      const repository = new engine.PipeSeriesRepository();
      repository.fillPipeSeries();

      const defaultSeries = buildPipeSerie(repository.getDefaultSerie());
      assert(defaultSeries, "No default pipe series available");
      return withDelete(repository, defaultSeries);
    }),
  getDefinitions(
    currentSelection?: PipeSeriesPreference,
    customSeries: CustomPipeSeries[] = [],
  ) {
    const allSeries = [
      ...normalizeCustomSeries(customSeries),
      ...pipesRepository.getAllPipeSeries(),
    ];
    assert(allSeries.length > 0, "No pipe series available");

    const defaultSeries = pipesRepository.getDefaultPipeSeries();

    const selectedSeriesId = currentSelection?.pipeSeries ?? defaultSeries.id;
    const selectedSeries =
      allSeries.find((s) => s.id === selectedSeriesId) || defaultSeries;
    const defaultPipe = defaultSeries.defaultPipe;

    return {
      pipes: {
        default: defaultPipe.id,
        disabled: false,
        id: "pipes",
        label: null,
        options: selectedSeries.pipes.map((pipe) => {
          return {
            default: pipe.id === selectedSeries.defaultPipe?.id,
            disabled: false,
            id: pipe.id,
            label: pipe.label,
            translationKey: pipe.translationKey,
          };
        }),
        translationKey: "field.pipes",
        type: "options",
      } satisfies OptionsFieldDefinition,
      pipeSeries: {
        default: defaultSeries.id,
        disabled: false,
        id: "pipeSeries",
        label: null,
        options: allSeries.map((series) => ({
          custom: series.custom,
          default: series.default,
          disabled: false,
          id: series.id,
          label: series.name,
          translationKey: null,
        })),
        translationKey: "field.pipeSeries",
        type: "options",
      } satisfies OptionsFieldDefinition<{ custom: boolean }>,
    };
  },
  getValveSizeId(
    pipeResult: PipeResults | null,
    pipeSeriesPreference: GetUsersPreferencesPipeSeriesStatus200 | undefined,
    customSeries: CustomPipeSeries[] = [],
  ) {
    const pipeSize = Object.values(pipeResult || {}).find(
      (value) => value.id === "size",
    )?.formattedValue;
    if (!pipeSize) return null;

    if (
      !pipeSeriesPreference ||
      pipeSeriesPreference?.pipeSeriesSource === "engine"
    ) {
      const allPipeSeries = pipesRepository.getAllPipeSeries();
      const preferredSeries =
        allPipeSeries.find(
          (series) => series.id === pipeSeriesPreference?.pipeSeriesId,
        ) || pipesRepository.getDefaultPipeSeries();
      const pipe = preferredSeries.pipes.find(
        (pipe) => pipe.label === pipeSize,
      );
      return pipe?.valveSize ?? null;
    }

    const pipeSeries = customSeries.find(
      (series) => series.id === pipeSeriesPreference.pipeSeries?.id,
    );
    assert(pipeSeries, "The custom pipe series could not be found");
    return (
      pipeSeries.pipes.find((pipe) => pipe.name === pipeSize)?.valveSize || null
    );
  },
  getValveSizeLabel(pipeSize: string | null) {
    if (!pipeSize) return null;
    const valveSizeOptions = this.getValveSizeOptions();
    const option = valveSizeOptions.find((option) => option.id === pipeSize);
    return option?.label ? `DN ${option.label}` : null;
  },
  getValveSizeOptions() {
    const repository = new engine.PipeSeriesRepository();
    const series = repository.createPipeSerie();
    assert(series, "Failed to create pipe series");

    const pipe = series.createPipe();
    assert(pipe, "Failed to create pipe");

    return withDelete(
      [repository, series, pipe],
      getOptionsFieldDefinition(pipe.valveSizes()).options,
    );
  },
  init(
    repository: PipeSeriesRepository,
    pipeSeriesPreference: PipeSeriesPreference,
    customPipeSeries: CustomPipeSeries[],
  ) {
    const definitions = pipesRepository.getDefinitions(
      pipeSeriesPreference,
      customPipeSeries,
    );
    const values = mergeWithDefaults(
      pipeSeriesPreference,
      extractDefaultValues(definitions),
    );
    const pipeSeries = definitions.pipeSeries.options.find(
      (series) => series.id === values.pipeSeries,
    );
    const pipe = definitions.pipes.options.find(
      (pipe) => pipe.id === values.pipes,
    );

    if (pipeSeries?.custom) {
      setCustomPipeSeries(repository, values, customPipeSeries);
    } else {
      setExistingPipeSeries(repository, {
        pipes: pipe?.id ?? null,
        pipeSeries: pipeSeries?.id ?? null,
      });
    }

    return repository;
  },
});
